import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { untranslatedString } from 'models/translated-string';
import { DateTime } from 'luxon';

import { CsPage, NavController } from 'providers/nav-controller';
import { PopupMenuProvider, PopupMenuSubsection } from 'providers/popup-menu/popup-menu';
import { AuthService } from 'services/auth.service';
import { SettingsService } from 'services/settings.service';
import { Gender } from '@cf-shared/enums';
import { getMeasureUnitConversion } from 'util/formatting';
import { exportFileAsync } from 'services/system-share';
import { LoggingService, errorReportFields } from 'services/logging.service';
import { DocWatcher } from 'services/database-wrapper';
import { UserDb } from 'services/user-db.service';
import { roundToPrecision } from '@cf-shared/shared-util';
import { getEntryLocalTimeInDesiredTimeZone, LogRecordType } from '@cf-shared/data-model/user-log';
import { Log_WeighIn, getAverageWaist, getAverageCalipersSum } from '@cf-shared/data-model/log-weigh-in';
import { Log_DayStart } from '@cf-shared/data-model/log-day-start';
import { buildProgressCsv } from './progress-csv';
import { DateRangeChangeEvent, TimePeriod, TimePeriodSet } from 'components/cs-date-range-toolbar/cs-date-range-toolbar';
import { dayNumber, theilSenChangePerWeek, TrendPoint } from '@cf-shared/body-comp-overview';
import { BodyCompOverviewDataService } from 'services/body-comp-overview-data.service';
import { BodyCompOverviewModel, numberText, OverviewPlanCheck, OverviewTrendKind } from 'services/body-comp-overview-model';
import { ConfigDbService } from 'services/config-db.service';
import { isCoachingDashboardOnAsync, watchCoachingDashboard } from 'services/coaching-dashboard-flag';
import { isRunningTrendChartsOnAsync, watchRunningTrendCharts } from 'services/running-trend-flag';
import { isDatabaseRecovering } from 'services/database-unavailable-error';
import { DebugService } from 'services/debug.service';
import { ProgressStatusRule } from 'services/progress-status';
import { isPlanCheckFailed } from 'services/server/plan-check-failure';
import { SvPlanHandlerService } from 'services/server/sv-plan-handler.service';
import { overviewPeriodForIncoming } from './progress-period';
import { ProgressChartSeries, ProgressChartDataPoint, ProgressChartAxisConfig, ProgressChartTrend } from 'components/cs-progress-chart/cs-progress-chart';
import { ChartDataBodyCompositionService } from 'services/chart-data-body-composition.service';
import { ChartDataNutritionService } from 'services/chart-data-nutrition.service';
import {
  PROGRESS_FILTERS,
  PROGRESS_FILTER_GROUPS,
  ProgressChartType,
  ProgressFilterId,
  ProgressFilterState,
  countActiveFilters,
  filterStateOnEnter,
  initialFilterState,
  isProgressFilterId,
  isSeriesHidden,
  toggleProgressFilter,
} from './progress-filters';

interface MeasurementData {
  currentValue: string;        // e.g., "88.0kg"
  totalChange: string;          // e.g., "-2.0kg" or "+1.5kg"
  avgChangePerWeek: string;     // e.g., "-0.5kg"
  changeText: string;           // e.g., "lost/week" or "gained/week"
  trendIcon: 'trending-down' | 'trending-up' | 'trending-flat';
  colorVar: string;             // CSS variable for chart color, e.g., "--chart-purple"
  hasData: boolean;             // true if we have measurements in range
}

export interface ProgressPageParams {
  timePeriod?: TimePeriod;
  chartType?: ProgressChartType;
}

@Component({
  selector: 'page-progress',
  templateUrl: 'progress.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ProgressPage extends CsPage<ProgressPageParams> implements OnDestroy {
  protected LL = LL;
  // time period selection
  timePeriod: TimePeriod = 'month';
  protected startDate: DateTime;
  protected endDate: DateTime;
  protected earliestDataDate?: DateTime;

  // chart type
  protected chartType: ProgressChartType = 'bodyComposition';
  protected chartTitle = '';

  // filter states, one per row of PROGRESS_FILTERS
  protected filters: ProgressFilterState = initialFilterState();

  // whether the chart and the measurements card show 7-day trailing averages
  protected get showAvg(): boolean {
    return this.filters['7dayAvg'];
  }

  // UI state
  protected activeFilterCount = 0;
  private filterPopoverSubsections: PopupMenuSubsection[] | null = null;

  // measurement data for measurements card
  protected weightMeasurement?: MeasurementData;
  protected waistMeasurement?: MeasurementData;
  protected skinfoldMeasurement?: MeasurementData;

  // the Body Composition overview (#5456), shown only while the coaching dashboard switch is on
  protected coachingDashboardOn = false;
  protected timePeriodSet = TimePeriodSet.Classic;
  protected overviewModel?: BodyCompOverviewModel;
  protected overviewLoading = false;
  protected overviewFailed = false;
  /** whether the selected range still ends today; the cards describe the present only then */
  private rangeEndsToday = true;
  /** counts overview loads, so an answer that arrives after a newer request is dropped */
  private overviewLoadCounter = 0;
  private unwatchCoachingDashboard?: () => void;

  // the running trend on the body composition chart (#6627), drawn only while its switch is on
  private runningTrendOn = false;
  protected chartTrends: ProgressChartTrend[] = [];
  /** counts trend loads, so an answer for an older range never replaces the trends of the range shown now */
  private trendLoadCounter = 0;
  private unwatchRunningTrend?: () => void;

  // reactive sync
  private docWatcher: DocWatcher;

  // CSV export data
  private csvData = '';

  // chart data
  protected chartSeries: ProgressChartSeries[] = [];
  protected chartData: ProgressChartDataPoint[] = [];
  protected chartAxisConfig: ProgressChartAxisConfig = { stacked: false };

  // loading state getter from data providers
  protected get chartLoading(): boolean {
    if (this.chartType === 'bodyComposition') {
      return this.bodyCompDataService.isLoading();
    } else {
      return this.nutritionDataService.isLoading();
    }
  }

  constructor(
    public navCtrl: NavController,
    protected auth: AuthService,
    private popupMenu: PopupMenuProvider,
    private cd: ChangeDetectorRef,
    private settings: SettingsService,
    private userDb: UserDb,
    private bodyCompDataService: ChartDataBodyCompositionService,
    private nutritionDataService: ChartDataNutritionService,
    private logging: LoggingService,
    private configDb: ConfigDbService,
    private overviewData: BodyCompOverviewDataService,
    private svPlanHandler: SvPlanHandlerService,
    private debug: DebugService,
  ) {
    super();
  }

  ngOnDestroy() {
    this.docWatcher?.destroy();
    this.unwatchCoachingDashboard?.();
    this.unwatchRunningTrend?.();
  }

  async ionViewCanEnter() {
    if (!this.docWatcher) {
      this.docWatcher = this.userDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    // the coaching dashboard switch is read BEFORE the first render, because the date range
    // toolbar works its range out once when it is created (#5456)
    this.coachingDashboardOn = await isCoachingDashboardOnAsync(this.configDb);
    this.timePeriodSet = this.coachingDashboardOn ? TimePeriodSet.WeekCounts : TimePeriodSet.Classic;
    if (!this.unwatchCoachingDashboard) {
      this.unwatchCoachingDashboard = watchCoachingDashboard(this.configDb, isOn => this.onCoachingDashboardSwitched(isOn));
    }
    this.runningTrendOn = await isRunningTrendChartsOnAsync(this.configDb);
    if (!this.unwatchRunningTrend) {
      this.unwatchRunningTrend = watchRunningTrendCharts(this.configDb, isOn => {
        if (this.viewDestroyed || isOn === this.runningTrendOn) return;
        this.runningTrendOn = isOn;
        // the chart and the Body Composition cards both draw the running trend
        void this.loadTrendsAsync();
        void this.loadOverviewAsync();
      });
    }

    // parse URL params
    const params = this.navParams.data;

    // time period: an incoming value is mapped onto the periods this page offers right now
    this.timePeriod = overviewPeriodForIncoming(params.timePeriod, this.coachingDashboardOn);

    // chart type
    if (params.chartType) {
      this.chartType = params.chartType;
    }

    // infer filter states from chartType; the 7-day average always starts off
    this.filters = filterStateOnEnter(this.chartType, this.filters);

    // initialize chart series based on chartType
    this.initializeChartSeries();

    // update UI state
    this.updateChartTitle();
    this.updateActiveFilterCount();

    // fetch earliest data date for "All" time period
    this.earliestDataDate = await this.userDb.getEarliestLogDateAsync();

    this.cd.detectChanges();
  }

  // initialize chart series and axis config based on chart type
  private initializeChartSeries() {
    if (this.chartType === 'bodyComposition') {
      this.chartSeries = this.bodyCompDataService.getSeries().map(s => ({
        ...s,
        hidden: this.getSeriesHiddenState(s.id)
      }));
      this.chartAxisConfig = this.bodyCompDataService.getAxisConfig();
    } else {
      this.chartSeries = this.nutritionDataService.getSeries().map(s => ({
        ...s,
        hidden: this.getSeriesHiddenState(s.id)
      }));
      this.chartAxisConfig = this.nutritionDataService.getAxisConfig();
    }
  }

  // get hidden state for a series based on current filter settings
  private getSeriesHiddenState(seriesId: string): boolean {
    return isSeriesHidden(this.filters, seriesId);
  }

  // update hidden flags on existing chart series
  private updateSeriesHiddenFlags() {
    this.chartSeries = this.chartSeries.map(s => ({
      ...s,
      hidden: this.getSeriesHiddenState(s.id)
    }));
  }

  // handle date range changes from the toolbar component
  protected async handleDateRangeChange(event: DateRangeChangeEvent) {
    this.timePeriod = event.timePeriod;
    this.startDate = event.startDate;
    this.endDate = event.endDate;
    this.rangeEndsToday = event.endsToday;

    // the overview loads beside the chart, not before it, so a slow overview never holds the chart back
    void this.loadOverviewAsync();
    void this.loadTrendsAsync();

    // fetch chart data
    await this.fetchChartDataAsync();

    // calculate measurements for measurements card
    await this.calculateMeasurementsAsync();
    this.cd.detectChanges();
  }

  /** the switch was flipped while the page is open: offer the other set of periods and start over */
  private onCoachingDashboardSwitched(isOn: boolean) {
    if (this.viewDestroyed || isOn === this.coachingDashboardOn) return;
    this.coachingDashboardOn = isOn;
    this.timePeriodSet = isOn ? TimePeriodSet.WeekCounts : TimePeriodSet.Classic;
    // changing the toolbar's inputs makes it recompute and emit a new range, which reloads everything
    this.timePeriod = overviewPeriodForIncoming(this.timePeriod, isOn);
    if (!isOn) {
      this.overviewModel = undefined;
    }
    this.cd.markForCheck();
  }

  /** load the Body Composition overview for the selected range (#5456); does nothing while the switch is off */
  protected async loadOverviewAsync() {
    if (!this.coachingDashboardOn || !this.startDate || !this.endDate) return;
    const loadNumber = ++this.overviewLoadCounter;
    this.overviewLoading = true;
    this.overviewFailed = false;
    this.cd.markForCheck();
    try {
      const isTwoWeeks = this.timePeriod === '2weeks';
      // the plan check judges the present moment only, so it is asked only for the range that ends today
      const planCheck = isTwoWeeks && this.rangeEndsToday ? await this.runPlanCheckForOverviewAsync() : undefined;
      const model = await this.overviewData.loadAsync({
        firstDayIso: this.startDate.toISODate(),
        lastDayIso: this.endDate.toISODate(),
        trendKind: isTwoWeeks ? OverviewTrendKind.Straight : OverviewTrendKind.Running,
        describesNow: this.rangeEndsToday,
        statusRule: ProgressStatusRule.Home,
        planCheck,
      }, this.docWatcher);
      // if a newer load started, or the page is gone, this answer is stale
      if (this.viewDestroyed || loadNumber !== this.overviewLoadCounter) return;
      this.overviewModel = model;
    } catch (err) {
      if (this.viewDestroyed || loadNumber !== this.overviewLoadCounter) return;
      // the card shows "could not load" with a Retry button either way; a database recovery
      // cutting the load short is reported under the same name as the skipped plan check below
      this.overviewFailed = true;
      if (isDatabaseRecovering(err)) {
        this.logging.trackSilentProblem('progressOverviewInterruptedByDatabaseRecovery', { database: err.databaseIdentifier });
      } else {
        this.logging.trackAbnormalException(err);
      }
    } finally {
      if (!this.viewDestroyed && loadNumber === this.overviewLoadCounter) {
        this.overviewLoading = false;
        this.cd.markForCheck();
      }
    }
  }

  /**
   * The plan check for the overview's status badge. A check that could not run is not an error
   * here: the cards then show the trends without a badge, as the Home goal card shows no status.
   */
  private async runPlanCheckForOverviewAsync(): Promise<OverviewPlanCheck | undefined> {
    try {
      return await this.svPlanHandler.checkForPlanAdjustments();
    } catch (error) {
      // the service already reported a plan check that could not run, under its own name. a
      // database recovery cutting a read short is reported as a problem, because nothing on this
      // page runs the check again by itself: the cards stay without a badge until the user picks
      // a range again or reopens the page
      if (isDatabaseRecovering(error)) {
        this.logging.trackSilentProblem('progressOverviewInterruptedByDatabaseRecovery', {
          database: error.databaseIdentifier,
        });
      } else if (!isPlanCheckFailed(error)) {
        this.logging.trackAbnormalException(error);
      }
      return undefined;
    }
  }

  /**
   * Load the running trend of the body composition chart (#6627). Only with the switch on, on the
   * body composition chart, and on ranges longer than 2 weeks; otherwise the chart gets no trends
   * and draws exactly as before. It loads beside the dots, so a slow or failed trend never holds
   * them back.
   */
  private async loadTrendsAsync() {
    const loadNumber = ++this.trendLoadCounter;
    const isShortRange = this.timePeriod === 'week' || this.timePeriod === '2weeks';
    if (!this.runningTrendOn || this.chartType !== 'bodyComposition' || isShortRange || !this.startDate || !this.endDate) {
      // a new empty array would still count as a change and rebuild the chart, so only clear trends that are there
      if (this.chartTrends.length > 0) {
        this.chartTrends = [];
        this.cd.markForCheck();
      }
      return;
    }
    try {
      const trends = await this.bodyCompDataService.fetchTrendsAsync({
        firstDayIso: this.startDate.toISODate(),
        lastDayIso: this.endDate.toISODate(),
        todayIso: this.debug.getNowLocal().toISODate(),
      }, this.docWatcher, true);
      // if a newer load started, or the page is gone, this answer is stale
      if (this.viewDestroyed || loadNumber !== this.trendLoadCounter) return;
      this.chartTrends = trends;
    } catch (err) {
      if (this.viewDestroyed || loadNumber !== this.trendLoadCounter) return;
      // the chart keeps its dots and lines without a trend; the failure must still reach a debugger
      this.chartTrends = [];
      this.logging.trackSilentProblem('progressTrendLoadFailed', { ...errorReportFields(err), timePeriod: this.timePeriod });
    }
    this.cd.markForCheck();
  }

  // fetch chart data from data providers
  private async fetchChartDataAsync() {
    if (!this.startDate || !this.endDate) return;

    const startDateStr = this.startDate.toISODate();
    const endDateStr = this.endDate.toISODate();

    if (this.chartType === 'bodyComposition') {
      // fetch with extended buffer for trailing average calculation
      this.chartData = await this.bodyCompDataService.fetchDataAsync(
        startDateStr,
        endDateStr,
        this.docWatcher,
        { extendedBuffer: this.showAvg ? 14 : 0 }
      );
    } else {
      this.chartData = await this.nutritionDataService.fetchDataAsync(
        startDateStr,
        endDateStr,
        this.docWatcher
      );
    }
  }

  // update chart title based on current chart type
  private updateChartTitle() {
    this.chartTitle = this.chartType === 'bodyComposition'
      ? LL.progress.chartTitles.bodyComposition()
      : LL.progress.chartTitles.nutrition();
  }

  // count active filters for badge
  private updateActiveFilterCount() {
    this.activeFilterCount = countActiveFilters(this.filters);
  }

  // handle settings button click
  protected async handleSettingsClick(event: Event) {
    const targetElement = event.currentTarget as HTMLElement;

    await this.popupMenu.createMenu({
      subsections: [
        {
          header: untranslatedString(''), // no header needed for single option
          buttons: [
            {
              text: LL.progress.exportAsCsv(),
              value: 'exportCsv',
              icon: 'download',
              selectState: false,
              handler: () => void this.exportAsCsv()
            }
          ]
        }
      ],
      selectMultiple: false,
      targetFromElement: targetElement,
      horizontalAlign: 'right',
    });
  }

  // export progress data as CSV
  private async exportAsCsv() {
    // generate CSV data
    await this.generateCsvDataAsync();

    // hand the file to the user (browser download, or the share sheet in the native apps)
    try {
      await exportFileAsync({ data: this.csvData, filename: 'progress.csv', mimeType: 'text/csv;charset=utf-8;' });
    } catch (err) {
      this.logging.trackAbnormalException(err, LL.errors.userFacing.exportFailed());
    }
  }

  // generate CSV data for the current date range
  private async generateCsvDataAsync() {
    // get user gender for caliper calculations
    const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
    const gender = immutableState.gender || Gender.male;

    // get unit conversions
    const locale = this.settings.locale;
    const weightConversion = getMeasureUnitConversion('bodyweight_kg', locale, 'kg');
    const tapeConversion = getMeasureUnitConversion('bodydimensions_cm', locale, 'cm');

    // fetch all log entries in the date range
    const entries = await this.userDb.getLogEntriesInRangeAsync(this.startDate, this.endDate, this.docWatcher);

    // separate weigh-ins and day-starts
    const weighIns = entries.filter(e => e.type === LogRecordType.WeighIn) as Log_WeighIn[];
    const dayStarts = entries.filter(e => e.type === LogRecordType.DayStart) as Log_DayStart[];

    // create maps by date for quick lookup
    const weighInsByDate = new Map<string, Log_WeighIn>();
    for (const weighIn of weighIns) {
      const date = DateTime.fromISO(weighIn.time_utc).toLocal().toISODate();
      weighInsByDate.set(date, weighIn);
    }

    const dayStartsByDate = new Map<string, Log_DayStart>();
    for (const dayStart of dayStarts) {
      const date = DateTime.fromISO(dayStart.time_utc).toLocal().toISODate();
      dayStartsByDate.set(date, dayStart);
    }

    // generate array of dates in range
    const dates: DateTime[] = [];
    let currentDate = this.startDate.startOf('day');
    const endDate = this.endDate.startOf('day');
    while (currentDate <= endDate) {
      dates.push(currentDate);
      currentDate = currentDate.plus({ days: 1 });
    }

    this.csvData = buildProgressCsv({ dates, weighInsByDate, dayStartsByDate, gender, weightConversion, tapeConversion });
  }

  // calculate measurement data for the measurements card
  private async calculateMeasurementsAsync() {
    // get user gender for caliper calculations
    const immutableState = await this.userDb.loadImmutableStateAsync(this.docWatcher);
    const gender = immutableState.gender || Gender.male;

    // get unit conversions
    const locale = this.settings.locale;
    const weightConversion = getMeasureUnitConversion('bodyweight_kg', locale, 'kg');
    const tapeConversion = getMeasureUnitConversion('bodydimensions_cm', locale, 'cm');

    // determine date range for fetching data
    // if showing trailing averages, load 14 extra days before startDate for the 7-day window
    const fetchStartDate = this.showAvg ? this.startDate.minus({ days: 14 }) : this.startDate;

    // fetch all weigh-in entries in the extended date range
    const entries = await this.userDb.getLogEntriesInRangeAsync(fetchStartDate, this.endDate, this.docWatcher);
    const weighIns = entries.filter(e => e.type === LogRecordType.WeighIn) as Log_WeighIn[];

    // calculate weeks in range for average per week calculation (always use visible range)
    const daysInRange = this.endDate.diff(this.startDate, 'days').days;
    const weeksInRange = daysInRange / 7;

    let weightData: (number | undefined)[];
    let waistData: (number | undefined)[];
    let skinfoldsData: (number | undefined)[];

    if (this.showAvg) {
      // build daily arrays for each measurement type
      const dailyArrays = this.buildDailyArrays(weighIns, fetchStartDate, this.endDate, gender);

      // apply trailing averages with full precision (rounding happens later)
      const weightAvg = this.calculateTrailingAverage(dailyArrays.weight);
      const waistAvg = this.calculateTrailingAverage(dailyArrays.waist);
      const skinfoldsAvg = this.calculateTrailingAverage(dailyArrays.skinfolds);

      // filter to visible date range (skip first 14 days used for trailing average warmup)
      const skipDays = 14;
      weightData = weightAvg.slice(skipDays);
      waistData = waistAvg.slice(skipDays);
      skinfoldsData = skinfoldsAvg.slice(skipDays);
    } else {
      // no averaging - extract values directly from weigh-ins
      weightData = weighIns.map(entry => entry.weight_kg);
      waistData = weighIns.map(entry => getAverageWaist(entry));
      skinfoldsData = weighIns.map(entry => getAverageCalipersSum(entry, gender));
    }

    // get chart colors from series configuration
    const series = this.bodyCompDataService.getSeries();
    const weightColor = series.find(s => s.id === 'weight')?.colorVar ?? '--chart-purple';
    const waistColor = series.find(s => s.id === 'waist')?.colorVar ?? '--chart-turquoise';
    const skinfoldColor = series.find(s => s.id === 'skinfold')?.colorVar ?? '--chart-blue';

    // calculate measurements from data arrays
    this.weightMeasurement = this.calculateSingleMeasurement(
      weightData,
      weightConversion,
      weeksInRange,
      weightColor
    );

    this.waistMeasurement = this.calculateSingleMeasurement(
      waistData,
      tapeConversion,
      weeksInRange,
      waistColor
    );

    this.skinfoldMeasurement = this.calculateSingleMeasurement(
      skinfoldsData,
      { factor: 1, unitName: 'mm' },
      weeksInRange,
      skinfoldColor
    );

    // #5456: with the coaching dashboard on, "per week" is a Theil-Sen slope over the raw daily
    // readings of the visible range, so one heavy morning at either end no longer moves it. The
    // (last - first) / weeks figure above stays exactly as it is while the switch is off.
    if (this.coachingDashboardOn) {
      const firstDayIso = this.startDate.toISODate();
      const lastDayIso = this.endDate.toISODate();
      const now = this.debug.getNowLocal();
      const rawPoints = (pick: (entry: Log_WeighIn) => number | undefined): TrendPoint[] => weighIns
        .map(entry => ({ dateIso: getEntryLocalTimeInDesiredTimeZone(entry, now).toISODate(), value: pick(entry) }))
        .filter(p => p.dateIso >= firstDayIso && p.dateIso <= lastDayIso && typeof p.value === 'number' && p.value > 0)
        .map(p => ({ x: dayNumber(p.dateIso), y: p.value }));
      this.applyTheilSenPerWeek(this.weightMeasurement, rawPoints(entry => entry.weight_kg), weightConversion);
      this.applyTheilSenPerWeek(this.waistMeasurement, rawPoints(entry => getAverageWaist(entry)), tapeConversion);
      this.applyTheilSenPerWeek(this.skinfoldMeasurement, rawPoints(entry => getAverageCalipersSum(entry, gender)), { factor: 1, unitName: 'mm' });
    }
  }

  /** replace a measurement's "per week" figure with the Theil-Sen change over the given readings */
  private applyTheilSenPerWeek(measurement: MeasurementData | undefined, points: TrendPoint[], conversion: { factor: number; unitName: string }) {
    if (!measurement) return;
    const perWeek = theilSenChangePerWeek(points);
    // if there are too few readings for a slope, show no rate at all, never the old maths
    if (perWeek === undefined) {
      measurement.avgChangePerWeek = '';
      measurement.changeText = '';
      return;
    }
    const converted = perWeek * conversion.factor;
    // one decimal, like every other figure on the coaching dashboard (#5456)
    measurement.avgChangePerWeek = numberText(converted, 1, true) + conversion.unitName;
    measurement.changeText = converted < 0 ? LL.progress.lostPerWeek() : LL.progress.gainedPerWeek();
  }

  // build daily arrays for each measurement type from weigh-in entries
  private buildDailyArrays(
    weighIns: Log_WeighIn[],
    startDate: DateTime,
    endDate: DateTime,
    gender: Gender
  ): { weight: (number | undefined)[]; waist: (number | undefined)[]; skinfolds: (number | undefined)[] } {
    const numDays = Math.ceil(endDate.diff(startDate, 'days').days) + 1;
    const weight: (number | undefined)[] = new Array(numDays).fill(undefined);
    const waist: (number | undefined)[] = new Array(numDays).fill(undefined);
    const skinfolds: (number | undefined)[] = new Array(numDays).fill(undefined);

    // map each weigh-in to its day index
    for (const entry of weighIns) {
      const entryDate = DateTime.fromISO(entry.time_utc).startOf('day');
      const dayIndex = Math.floor(entryDate.diff(startDate.startOf('day'), 'days').days);

      // ensure index is within bounds
      if (dayIndex >= 0 && dayIndex < numDays) {
        weight[dayIndex] = entry.weight_kg;
        waist[dayIndex] = getAverageWaist(entry);
        skinfolds[dayIndex] = getAverageCalipersSum(entry, gender);
      }
    }

    return { weight, waist, skinfolds };
  }

  // helper to calculate measurement data for a single measurement type
  private calculateSingleMeasurement(
    data: (number | undefined)[],
    conversion: { factor: number; unitName: string },
    weeksInRange: number,
    colorVar: string
  ): MeasurementData | undefined {
    // filter out undefined values
    const validValues = data.filter(v => v !== undefined) as number[];

    // if no valid values, return undefined (measurement never logged)
    if (validValues.length === 0) {
      return undefined;
    }

    // get first and last values (array is already chronologically ordered)
    const firstValue = validValues[0];
    const lastValue = validValues[validValues.length - 1];

    // convert to user's preferred units
    const firstConverted = firstValue * conversion.factor;
    const lastConverted = lastValue * conversion.factor;

    // calculate total change
    const totalChange = lastConverted - firstConverted;

    // calculate average change per week
    const avgChangePerWeek = weeksInRange > 0 ? totalChange / weeksInRange : 0;

    // determine trend icon
    let trendIcon: 'trending-down' | 'trending-up' | 'trending-flat';
    if (Math.abs(totalChange) < 0.05) {
      trendIcon = 'trending-flat';
    } else if (totalChange < 0) {
      trendIcon = 'trending-down';
    } else {
      trendIcon = 'trending-up';
    }

    // determine change text (translated)
    const changeText = totalChange < 0 ? LL.progress.lostPerWeek() : LL.progress.gainedPerWeek();

    // format values with explicit rounding before toFixed
    const currentValue = `${roundToPrecision(lastConverted, 0.1).toFixed(1)}${conversion.unitName}`;
    const totalChangeFormatted = `${totalChange >= 0 ? '+' : ''}${roundToPrecision(totalChange, 0.1).toFixed(1)}`; // no unit in brackets
    const avgChangePerWeekFormatted = `${avgChangePerWeek >= 0 ? '+' : ''}${roundToPrecision(avgChangePerWeek, 0.01).toFixed(2)}${conversion.unitName}`;

    return {
      currentValue,
      totalChange: totalChangeFormatted,
      avgChangePerWeek: avgChangePerWeekFormatted,
      changeText,
      trendIcon,
      colorVar,
      hasData: true
    };
  }

  // calculate 7-day trailing average for data array
  private calculateTrailingAverage(data: (number | undefined)[], windowSize: number = 7): (number | undefined)[] {
    // calculate 7-day trailing average (current day + 6 previous days) with full precision
    return data.map((_, index) => {
      const startIdx = Math.max(0, index - windowSize + 1);
      const window = data.slice(startIdx, index + 1).filter(v => v !== undefined) as number[];
      if (window.length > 0) {
        const average = window.reduce((a, b) => a + b, 0) / window.length;
        return average;
      }
      return undefined;
    });
  }

  // open filter popover
  protected async handleFilterButtonClick(event: Event) {
    const targetElement = event.currentTarget as HTMLElement;

    // create subsections for filter popover
    const subsections: PopupMenuSubsection[] = PROGRESS_FILTER_GROUPS.map(({ group, header }) => ({
      header: header(),
      buttons: PROGRESS_FILTERS.filter(f => f.group === group).map(f => ({
        text: f.label(),
        description: f.description?.(),
        value: f.id,
        selectState: this.filters[f.id],
        helpIcon: f.description !== undefined ? true : undefined,
        handler: () => this.toggleFilter(f.id),
      })),
    }));

    // store subsections reference for state updates
    this.filterPopoverSubsections = subsections;

    await this.popupMenu.createMenu({
      subsections: subsections,
      selectMultiple: true,
      targetFromElement: targetElement,
      horizontalAlign: 'right',
    });

    // clear subsections reference when popover is dismissed
    this.filterPopoverSubsections = null;
  }

  // toggle individual filter
  private async toggleFilter(filterName: ProgressFilterId) {
    const previousChartType = this.chartType;

    // toggle the filter; a filter of the other chart clears this chart's filters and switches chart
    const toggled = toggleProgressFilter(this.filters, filterName, this.chartType);
    this.filters = toggled.state;
    this.chartType = toggled.chartType;

    // if the 7-day average changed, refetch chart data with the new buffer and recalculate measurements
    if (filterName === '7dayAvg') {
      await this.fetchChartDataAsync();
      await this.calculateMeasurementsAsync();
    }

    // if chart type changed, re-initialize series and fetch new data
    if (this.chartType !== previousChartType) {
      this.initializeChartSeries();
      void this.loadTrendsAsync();
      await this.fetchChartDataAsync();
    } else {
      // just update hidden flags on existing series
      this.updateSeriesHiddenFlags();
    }

    // sync state back to popover buttons
    if (this.filterPopoverSubsections) {
      for (const subsection of this.filterPopoverSubsections) {
        for (const button of subsection.buttons) {
          // update all button selectStates to match current component state
          if (isProgressFilterId(button.value)) {
            button.selectState = this.filters[button.value];
          }
        }
      }
    }

    // update chart title and active filter count
    this.updateChartTitle();
    this.updateActiveFilterCount();
    this.cd.detectChanges();
  }

}
